import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalyroLogo } from "@/components/logo/CalyroLogo";
import { HeroDashboardAnimated } from "@/components/HeroDashboardAnimated";
import { RevenueImpactSection } from "@/components/RevenueImpactSection";

const metrics = [
  { label: "Call Answer Rate", value: "98%" },
  { label: "Revenue Increase", value: "40%" },
  { label: "Always Available", value: "24/7" },
  { label: "Avg Response Time", value: "5 sec" },
];

const features = [
  {
    title: "24/7 AI Receptionist",
    description:
      "Never miss a call again. Your AI receptionist handles calls, schedules appointments, and captures leads around the clock.",
    iconBg: "from-indigo-500 to-sky-500",
  },
  {
    title: "Smart Booking Engine",
    description:
      "Real-time calendar sync with custom rules, buffer times, and automatic conflict handling for every location.",
    iconBg: "from-pink-500 to-rose-500",
  },
  {
    title: "Revenue Growth",
    description:
      "Turn more calls into bookings with intent-aware flows that prioritize high-value callers automatically.",
    iconBg: "from-emerald-500 to-lime-500",
  },
  {
    title: "Enterprise Security",
    description:
      "HIPAA-grade security, encryption in transit and at rest, role-based access, and detailed audit trails.",
    iconBg: "from-orange-500 to-amber-500",
  },
  {
    title: "Intelligent AI",
    description:
      "Powered by state-of-the-art language models tuned for service businesses, with strict guardrails and rules.",
    iconBg: "from-sky-500 to-cyan-500",
  },
  {
    title: "Real-time Analytics",
    description:
      "Track every call, lead, and booking with a live dashboard that shows what's working in plain English.",
    iconBg: "from-violet-500 to-fuchsia-500",
  },
];

const industries = [
  "Dental Clinics",
  "Med Spas",
  "Hair Salons",
  "Veterinary Clinics",
  "Chiropractors",
  "Massage Therapy",
  "Beauty Salons",
  "Auto Repair",
];

const testimonials = [
  {
    quote:
      "Calyro.ai increased our bookings by 35% in the first month. It's like having a full-time receptionist for a fraction of the cost.",
    name: "Dr. Sarah Mitchell",
    role: "Owner, Bright Smile Dental",
    initial: "S",
  },
  {
    quote:
      "Our clients love the instant booking experience. No more phone tag or missed calls during busy periods.",
    name: "Maria Rodriguez",
    role: "Director, Serenity Med Spa",
    initial: "M",
  },
  {
    quote:
      "The AI understands our services better than some new staff. Support is responsive and the product keeps getting better.",
    name: "James Chen",
    role: "Owner, Elite Auto Repair",
    initial: "J",
  },
];

function StatusDot({ color }) {
  return <span className={`h-4 w-4 rounded-full border-2 border-slate-900 ${color}`} />;
}

function BadgeText({ children }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-[11px] text-slate-700 ring-1 ring-slate-300/70">
      <span className="text-xs">✓</span>
      <span>{children}</span>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border-2 border-white/50 bg-white/90 backdrop-blur-md p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all">
      <p className="text-xs font-semibold text-slate-700">{label}</p>
      <p className="mt-1 text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{value}</p>
    </div>
  );
}

function ChatBubble({ from, text, side, highlight }) {
  const isRight = side === "right";
  return (
    <div className={`flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[80%] rounded-2xl px-3 py-2 text-[11px]",
          isRight ? "rounded-br-sm" : "rounded-bl-sm",
          highlight
            ? "bg-gradient-to-tr from-indigo-500 to-sky-500 text-slate-50"
            : "bg-slate-800/90 text-slate-100",
        ].join(" ")}
      >
        <p className="mb-1 text-[10px] font-semibold opacity-80">{from}</p>
        <p>{text}</p>
      </div>
    </div>
  );
}

function FooterColumn({ title, links }) {
  // Map link text to routes
  const linkRoutes = {
    "Features": "/features",
    "Pricing": "/pricing",
    "Demo": "/demo",
    "FAQ": "/faq",
    "About Us": "#",
    "Contact": "#",
    "Careers": "#",
    "Help Center": "/faq",
    "API Docs": "#",
    "Privacy Policy": "/privacy",
    "Terms of Service": "/terms",
    "Refund Policy": "/refund-policy"
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-slate-900">{title}</p>
      <ul className="space-y-1 text-xs text-slate-600">
        {links.map((l) => (
          <li key={l}>
            {linkRoutes[l] ? (
              <Link to={linkRoutes[l]} className="cursor-pointer hover:text-slate-900 transition-colors">
                {l}
              </Link>
            ) : (
              <span className="cursor-pointer hover:text-slate-900">
                {l}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

const container = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.19, 1, 0.22, 1],
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export function HomePage() {
  return (
    <main className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-16 sm:pt-20">
        {/* Animated gradient overlay */}
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -inset-x-32 -top-32 h-80 bg-[radial-gradient(circle_at_top,_#3b82f6_0,_transparent_70%)] blur-3xl" />
          <div className="absolute -inset-x-32 -bottom-40 h-96 bg-[radial-gradient(circle_at_bottom,_#8b5cf6_0,_transparent_70%)] blur-3xl" />
        </div>

        {/* Slow moving subtle gradient */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,#60a5fa,#8b5cf6,#ec4899)] bg-[length:200%_200%] mix-blend-soft-light animate-bg-pan-slow opacity-30" />

        {/* Circuit Board background */}
        <div className="pointer-events-none absolute inset-0">
          {/* Main circuit lines */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.08)_1px,transparent_1px)] bg-[size:60px_60px]" />
          
          {/* Secondary offset lines for depth */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.06)_1px,transparent_1px)] bg-[size:120px_120px] translate-x-[30px] translate-y-[30px]" />
          
          {/* Circuit nodes/dots at intersections */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.15)_1.5px,transparent_1.5px)] bg-[size:60px_60px]" />
          
          {/* Larger accent nodes */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.2)_2px,transparent_2px)] bg-[size:180px_180px] translate-x-[90px] translate-y-[90px]" />
          
          {/* Diagonal circuit traces */}
          <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_58px,rgba(99,102,241,0.04)_58px,rgba(99,102,241,0.04)_60px)]" />
          <div className="absolute inset-0 bg-[repeating-linear-gradient(-45deg,transparent,transparent_58px,rgba(139,92,246,0.04)_58px,rgba(139,92,246,0.04)_60px)]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pb-8 sm:pb-12 pt-4 sm:pt-6 lg:pt-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* LEFT SIDE */}
            <motion.div
              className="space-y-5 sm:space-y-8"
              variants={container}
              initial="hidden"
              animate="visible"
            >
              {/* Trusted badge */}
              <motion.div
                variants={item}
                className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/80 px-4 py-2 text-xs font-medium text-blue-700 shadow-sm backdrop-blur"
              >
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Revenue-Critical Call Control</span>
              </motion.div>

              {/* Headline */}
              <motion.div variants={item} className="space-y-4 sm:space-y-5">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
                  <span className="text-slate-900">Every Missed Call</span>
                  <br />
                  <span className="text-slate-900">Is </span>
                  <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">Lost Revenue.</span>
                </h1>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed max-w-lg font-medium">
                  Calyro <span className="text-blue-600 font-semibold">controls</span> every conversation that makes you money — answering, qualifying, booking, escalating, and logging revenue-critical calls using deterministic rules you control.
                </p>
              </motion.div>

              {/* CTAs */}
              <motion.div
                variants={item}
                className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
              >
                <Link to="/onboarding" className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold text-white shadow-lg shadow-blue-500/40 transition-all hover:shadow-xl hover:shadow-purple-500/50 hover:scale-105">
                  <span className="relative z-10">Start My Call Control Setup</span>
                  <svg className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-purple-600 to-pink-600 transition-opacity" />
                </Link>

                <Link to="/demo" className="inline-flex items-center justify-center gap-3 rounded-full border-2 border-slate-300 bg-white px-5 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50 backdrop-blur-sm transition-all shadow-md hover:shadow-lg">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Hear Real Calls
                </Link>
              </motion.div>

              {/* Brutal differentiator */}
              <motion.p
                variants={item}
                className="text-sm text-slate-500 font-medium"
              >
                No scripts. No prompt guessing. No black-box AI decisions.
              </motion.p>

              {/* Trust badges - Control/Risk Reducer Signals */}
              <motion.div
                variants={item}
                className="flex flex-wrap items-center gap-6 pt-2"
              >
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span>Deterministic call flows</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center">
                    <svg className="w-3 h-3 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span>Human escalation built-in</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                    <svg className="w-3 h-3 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span>Full audit logs</span>
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT SIDE – Animated Dashboard */}
            <HeroDashboardAnimated />
          </div>

          {/* Stats bar - moved inside */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {metrics.map((m, idx) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.9 + idx * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex flex-col items-center">
                  <p className="text-4xl md:text-5xl font-bold text-slate-900 mb-2 group-hover:scale-110 transition-transform">
                    {m.value}
                  </p>
                  <p className="text-xs text-slate-600 uppercase tracking-wider">{m.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURES - STRIPE-INSPIRED BOLD DESIGN */}
      <section id="features" className="relative bg-white overflow-hidden">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50"></div>
        
        {/* Animated glow effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-1/2 -left-1/4 w-[800px] h-[800px] bg-gradient-to-tr from-violet-500/10 to-fuchsia-500/10 rounded-full blur-3xl"></div>
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          
          {/* Feature 1: AI Receptionist - Left aligned */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-20 border-b border-slate-200"
          >
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-4xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight leading-[1.1]"
              >
                Your Revenue Line, <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">Under Control.</span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-base lg:text-lg text-slate-600 leading-relaxed mb-10"
              >
                Calyro doesn't just answer calls — it enforces how calls are handled. Every conversation follows deterministic rules you define, ensuring high-value calls are captured, escalated, and logged with full visibility.
              </motion.p>

              <div className="space-y-4 mb-8">
                {[
                  { title: "Deterministic Call Flows", desc: "Calls follow rules you define, not prompts that guess. Every path is predictable and auditable." },
                  { title: "Revenue-Safe Escalation", desc: "High-value or complex calls escalate to your team automatically — no missed opportunities." },
                  { title: "Full Call Audit Trail", desc: "Every call is logged, transcribed, and reviewable. Complete visibility into every conversation." },
                  { title: "Zero Busy Signals", desc: "Unlimited concurrency without losing priority handling. Every caller connects instantly." }
                ].map((feature, idx) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 + idx * 0.1 }}
                    whileHover={{ x: 5, transition: { duration: 0.2 } }}
                    className="flex items-start gap-3 group cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-blue-500/20 group-hover:scale-110 transition-all duration-300">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-slate-900 font-semibold mb-1 text-sm">{feature.title}</p>
                      <p className="text-slate-600 text-sm">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <Link to="/demo" className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-blue-500/50 hover:shadow-xl hover:shadow-blue-500/60 hover:scale-105 transition-all">
                  See How Call Control Works
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </motion.div>
            </motion.div>

            {/* Floating dashboard card */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-3xl blur-3xl"
              />
              <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl p-8 hover:shadow-3xl transition-shadow duration-500">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-600 font-semibold">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span>Live</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200 cursor-pointer"
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.15, 1],
                        rotate: [0, 15, -15, 0]
                      }}
                      transition={{ 
                        duration: 1.5, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Incoming call...</p>
                      <p className="text-xs text-slate-600">Dr. Smith - Dental Cleaning</p>
                    </div>
                    <motion.div
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-xs font-semibold text-emerald-600"
                    >
                      Active
                    </motion.div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    whileHover={{ scale: 1.02, x: 5, transition: { duration: 0.2 } }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                      <span className="text-lg">✅</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Booking confirmed</p>
                      <p className="text-xs text-slate-600">Sarah M. - Thu 3:30 PM</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    whileHover={{ scale: 1.02, x: 5, transition: { duration: 0.2 } }}
                    className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">Lead captured</p>
                      <p className="text-xs text-slate-600">James K. - New patient inquiry</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Feature 2: Smart Booking - Right aligned */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-20 border-b border-slate-200"
          >
            {/* Visual first on desktop */}
            <motion.div
              initial={{ opacity: 0, x: -40, scale: 0.95 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative lg:order-1 order-2"
            >
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
                className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 to-rose-500/20 rounded-3xl blur-3xl"
              />
              <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl p-8 hover:shadow-3xl transition-shadow duration-500">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-slate-900 font-semibold">Calendar Sync</h3>
                  <div className="px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-xs text-pink-600 font-semibold">Real-time</div>
                </div>
                
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                    <div key={i} className="text-center text-xs text-slate-600 font-medium">{day}</div>
                  ))}
                </div>
                
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({length: 35}).map((_, i) => {
                    const isBooked = [8, 9, 15, 16, 22].includes(i);
                    const isAvailable = [10, 11, 17, 18, 23, 24].includes(i);
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.5 + (i * 0.02) }}
                        whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm cursor-pointer transition-all ${
                          isBooked ? 'bg-pink-500/20 border border-pink-500/40 text-pink-600 font-semibold hover:bg-pink-500/30' :
                          isAvailable ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20' :
                          'bg-slate-50 border border-slate-200 text-slate-400 hover:bg-slate-100'
                        }`}
                      >
                        {i < 7 ? '' : i - 6}
                      </motion.div>
                    );
                  })}
                </div>
                
                <div className="mt-6 flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-pink-500/40"></div>
                    <span className="text-slate-600">Booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-500/20"></div>
                    <span className="text-slate-600">Available</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:order-2 order-1"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="inline-flex items-center gap-2 rounded-full bg-pink-500/10 border border-pink-500/20 px-4 py-1.5 mb-5"
              >
                <span className="text-xs font-semibold text-pink-600 uppercase tracking-wider">
                  No Conflicts. Ever.
                </span>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-3xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight leading-[1.15]"
              >
                Your Rules. Your Calendar.
                <br />
                <span className="bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent">Zero Double-Bookings.</span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-base text-slate-600 leading-relaxed mb-6 max-w-md"
              >
                Calyro validates every booking against your real-world logic — staff schedules, room availability, service duration, and buffer times — in real time.
              </motion.p>

              <div className="space-y-3 mb-6">
                {[
                  { title: "Provider & Room Management", desc: "Assigns the right staff and resources automatically." },
                  { title: "Smart Buffers & Prep Time", desc: "Adds cleanup, prep, or travel time automatically." },
                  { title: "Real-Time 2-Way Sync", desc: "Manual changes reflect instantly — no conflicts." },
                  { title: "Multi-Location Intelligence", desc: "Prevents overlaps across locations and teams." }
                ].map((feature, idx) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 + idx * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-slate-900 font-medium text-sm">{feature.title}</span>
                      <span className="text-slate-500 text-sm"> — {feature.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 1.1 }}
                className="text-sm text-slate-400 pt-2"
              >
                Let AI enforce your scheduling logic — so you never fix booking mistakes again.
              </motion.p>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* Feature 3: Revenue Impact - New Dark Component */}
      <RevenueImpactSection />

      {/* INDUSTRIES - ENHANCED */}
      <section id="solutions" className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-20 sm:py-24">
        {/* Background decorative elements */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 border border-blue-500/20 px-4 py-2 mb-6"
            >
              <span className="text-2xl">🏢</span>
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Trusted by Industry Leaders</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6"
            >
              Built for{" "}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Service Businesses
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed mb-12"
            >
              Trusted by leading businesses across multiple industries to deliver exceptional
              customer experiences and capture every opportunity.
            </motion.p>
          </div>

          {/* Industry Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
            {industries.map((industry, idx) => {
              const iconComponents = {
                "Dental Clinics": (
                  <svg className="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C9.243 2 7 4.243 7 7v6c0 1.654-1.346 3-3 3v2c2.757 0 5-2.243 5-5V7c0-1.654 1.346-3 3-3s3 1.346 3 3v6c0 2.757 2.243 5 5 5v-2c-1.654 0-3-1.346-3-3V7c0-2.757-2.243-5-5-5z"/>
                  </svg>
                ),
                "Med Spas": (
                  <svg className="w-10 h-10 text-violet-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L8 8h3v12h2V8h3l-4-6z"/>
                    <path d="M8 14H6v8h2v-8zM18 14h-2v8h2v-8z"/>
                  </svg>
                ),
                "Hair Salons": (
                  <svg className="w-10 h-10 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2v14H3v3c0 1.66 1.34 3 3 3h12c1.66 0 3-1.34 3-3V2l-1.5 1.5zM15 20H6c-.55 0-1-.45-1-1v-1h10v2zm4-1c0 .55-.45 1-1 1s-1-.45-1-1v-3H8V5h11v14z"/>
                  </svg>
                ),
                "Veterinary Clinics": (
                  <svg className="w-10 h-10 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.5 12.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5zm.5-5c0 .83-.67 1.5-1.5 1.5S2 8.33 2 7.5 2.67 6 3.5 6 5 6.67 5 7.5zm5.5 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5S11 8.33 11 7.5zm11 5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5.67-1.5 1.5-1.5 1.5.67 1.5 1.5zm-3-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 10c-2.33 0-7 1.17-7 3.5V16c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                ),
                "Chiropractors": (
                  <svg className="w-10 h-10 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C11.5 2 11 2.19 10.59 2.59L2.59 10.59C1.8 11.37 1.8 12.63 2.59 13.41L10.59 21.41C11.37 22.2 12.63 22.2 13.41 21.41L21.41 13.41C22.2 12.63 22.2 11.37 21.41 10.59L13.41 2.59C13 2.19 12.5 2 12 2M12 4L20 12L12 20L4 12M12 7C10.9 7 10 7.9 10 9S10.9 11 12 11 14 10.1 14 9 13.1 7 12 7M9 12C8.45 12 8 12.45 8 13V15C8 15.55 8.45 16 9 16S10 15.55 10 15V13C10 12.45 9.55 12 9 12M15 12C14.45 12 14 12.45 14 13V15C14 15.55 14.45 16 15 16S16 15.55 16 15V13C16 12.45 15.55 12 15 12Z"/>
                  </svg>
                ),
                "Massage Therapy": (
                  <svg className="w-10 h-10 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3L2 12h3v8h14v-8h3L12 3zm0 2.7L17.3 11H16v7h-2v-5h-4v5H8v-7H6.7L12 5.7z"/>
                  </svg>
                ),
                "Beauty Salons": (
                  <svg className="w-10 h-10 text-rose-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9.13 12c.59.73 1.35 1.27 2.17 1.62L10.18 18H9l-3 3h12l-3-3h-1.18l-1.12-4.38c.82-.35 1.58-.89 2.17-1.62l3.45 1.38c.46.18.97-.05 1.15-.51.18-.46-.05-.97-.51-1.15l-3.2-1.28c.17-.34.29-.72.36-1.12L20 8.5V4h-3v2.5l-2.93.82c-.26-.81-.78-1.49-1.48-1.94L13 3c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2l.41 2.38c-.7.45-1.22 1.13-1.48 1.94L3 6.5V4H0v4.5l3.91 1.32c.07.4.19.78.36 1.12l-3.2 1.28c-.46.18-.69.69-.51 1.15.18.46.69.69 1.15.51L5.13 12z"/>
                  </svg>
                ),
                "Auto Repair": (
                  <svg className="w-10 h-10 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                  </svg>
                )
              };
              
              const gradients = {
                "Dental Clinics": "from-blue-500/10 to-sky-500/10",
                "Med Spas": "from-violet-500/10 to-purple-500/10",
                "Hair Salons": "from-pink-500/10 to-rose-500/10",
                "Veterinary Clinics": "from-emerald-500/10 to-teal-500/10",
                "Chiropractors": "from-indigo-500/10 to-blue-500/10",
                "Massage Therapy": "from-purple-500/10 to-fuchsia-500/10",
                "Beauty Salons": "from-rose-500/10 to-pink-500/10",
                "Auto Repair": "from-amber-500/10 to-orange-500/10"
              };
              
              return (
                <motion.div
                  key={industry}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ y: -8, scale: 1.05, transition: { duration: 0.2 } }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-lg hover:shadow-2xl hover:border-blue-300 transition-all duration-300 cursor-pointer"
                >
                  {/* Gradient overlay on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradients[industry]} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                    <motion.div
                      whileHover={{ rotate: [0, -5, 5, 0], scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                      className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center group-hover:shadow-lg transition-shadow"
                    >
                      {iconComponents[industry]}
                    </motion.div>
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                      {industry}
                    </h3>
                  </div>

                  {/* Checkmark indicator */}
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-center"
          >
            <p className="text-slate-600 mb-6">Don't see your industry? Calyro adapts to any service business.</p>
            <Link to="/onboarding" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/50 hover:shadow-xl hover:shadow-blue-500/60 hover:scale-105 transition-all">
              See how Calyro works for you
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS - ENHANCED */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50 to-blue-50 py-20 sm:py-24">
        {/* Background decorative elements */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 right-20 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-violet-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl"></div>
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 mb-6"
            >
              <span className="text-2xl">💬</span>
              <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Customer Stories</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6"
            >
              Loved by{" "}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Business Owners
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed mb-12"
            >
              See how Calyro.ai is transforming service businesses and helping them grow.
            </motion.p>
          </div>

          <div className="grid gap-6 md:grid-cols-3 lg:gap-8">
            {testimonials.map((t, idx) => (
              <motion.article
                key={t.name}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
                className="group relative flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-8 shadow-lg hover:shadow-2xl hover:border-blue-300 transition-all duration-300"
              >
                {/* Quote icon background */}
                <div className="absolute top-6 right-6 text-6xl text-blue-500/10 font-serif leading-none">"</div>
                
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>

                <div className="relative z-10">
                  {/* Stars */}
                  <div className="mb-4 flex items-center gap-1">
                    {"★★★★★".split("").map((star, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0, rotate: -180 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true }}
                        transition={{ 
                          duration: 0.5, 
                          delay: idx * 0.15 + i * 0.08,
                          type: "spring",
                          stiffness: 200
                        }}
                        className="text-lg text-amber-400"
                      >
                        {star}
                      </motion.span>
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-slate-700 leading-relaxed mb-6 text-base">
                    "{t.quote}"
                  </p>
                </div>

                {/* Author */}
                <div className="relative z-10 flex items-center gap-4 pt-6 border-t border-slate-200">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-white text-lg font-bold shadow-lg"
                  >
                    {t.initial}
                  </motion.div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-600">{t.role}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Trust indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-6 px-6 py-4 rounded-2xl bg-white border border-slate-200 shadow-lg">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white"></div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 border-2 border-white"></div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white"></div>
                </div>
                <span className="text-sm font-semibold text-slate-900">500+</span>
              </div>
              <div className="h-8 w-px bg-slate-200"></div>
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Businesses trust Calyro</span> to handle their calls
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA + FOOTER - REDESIGNED */}
      <section className="relative overflow-hidden border-t border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-24 sm:py-32">
        {/* Animated gradient orbs */}
        <div className="pointer-events-none absolute inset-0">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -top-40 -left-40 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute -bottom-40 -right-40 w-96 h-96 bg-violet-500/30 rounded-full blur-3xl"
          />
        </div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none"></div>

        <div className="relative mx-auto max-w-5xl px-4 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500/20 to-violet-500/20 border border-blue-400/30 px-4 py-2 mb-8 backdrop-blur-sm"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-xl"
              >
                ⚡
              </motion.span>
              <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Limited Time Offer</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
            >
              Ready to Transform
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                Your Business?
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10"
            >
              Join hundreds of businesses using Calyro.ai to capture more leads, book more
              appointments, and deliver better customer experiences.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
            >
              <Link
                to="/onboarding"
                className="group relative w-full sm:w-auto overflow-hidden rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-8 py-4 text-base font-semibold text-white shadow-2xl shadow-blue-500/50 hover:shadow-blue-500/70 hover:scale-105 transition-all duration-300"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Launch My AI Agent
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </Link>
              <a
                href="mailto:hello@calyro.ai"
                className="w-full sm:w-auto rounded-full border-2 border-white/30 bg-white/5 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                Talk to Sales
              </a>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap items-center justify-center gap-8 text-sm"
            >
              {[
                { icon: "🔒", text: "Secure & HIPAA Compliant" },
                { icon: "⚡", text: "White-glove setup included" },
                { icon: "💬", text: "Human support when you need it" }
              ].map((item, idx) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 + idx * 0.1 }}
                  whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                  className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-3 backdrop-blur-sm"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-white font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-10 border-t border-slate-200 bg-slate-50">
          <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 py-10 text-sm text-slate-700 lg:flex-row lg:justify-between lg:px-0">
            <div className="space-y-3">
              <Link to="/" className="flex items-center gap-2">
                <CalyroLogo className="h-14" showText={true} textClassName="text-xl font-semibold" transparent={true} />
              </Link>
              <p className="max-w-xs text-xs text-slate-600">
                AI receptionists that answer every call, qualify every lead, and book more
                appointments—without adding headcount.
              </p>
            </div>

            <div className="grid flex-1 grid-cols-2 gap-8 md:grid-cols-4">
              <FooterColumn
                title="Company"
                links={["About Us", "Contact", "Careers"]}
              />
              <FooterColumn
                title="Product"
                links={["Features", "Pricing", "Demo", "FAQ"]}
              />
              <FooterColumn
                title="Resources"
                links={["Help Center", "API Docs"]}
              />
              <FooterColumn
                title="Legal"
                links={["Privacy Policy", "Terms of Service", "Refund Policy"]}
              />
            </div>

            <div className="space-y-3 text-xs">
              <p className="font-semibold text-slate-900">Contact Us</p>
              <a href="mailto:support@calyro.ai" className="block text-slate-600 hover:text-slate-900 transition-colors">
                support@calyro.ai
              </a>
              <a href="tel:+15551234567" className="block text-slate-600 hover:text-slate-900 transition-colors">
                (555) 123-4567
              </a>
              <div className="flex gap-3 text-slate-600">
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 transition-colors cursor-pointer">
                  𝕏
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 transition-colors cursor-pointer">
                  in
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900 transition-colors cursor-pointer">
                  ▶
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200 py-4 text-center text-[11px] text-slate-600">
            © {new Date().getFullYear()} Calyro.ai. All rights reserved.
          </div>
        </footer>
      </section>
    </main>
  );
}